import { SAMLAuthProvider, signInWithCustomToken, signInWithPopup, getAdditionalUserInfo, updateProfile } from 'firebase/auth';
import { auth } from './firebase';
import { postIdTokenToSessionLogin } from './session';

/** Firebase Identity Platform SAML provider configured for DHSB Workspace. */
export const SAML_PROVIDER_ID = 'saml.dhsb-account';

const samlProvider = new SAMLAuthProvider(SAML_PROVIDER_ID);

function normalizeGroupMembership(value) {
    if (!value) {
        return [];
    }
    if (Array.isArray(value)) {
        return value.map((g) => String(g).trim()).filter(Boolean);
    }
    if (typeof value === 'string') {
        return value.split(',').map((g) => g.trim()).filter(Boolean);
    }
    return [];
}

function safeString(value) {
    if (typeof value === 'string' && value.trim()) {
        return value.trim();
    }
    if (Array.isArray(value) && value.length === 1) {
        return safeString(value[0]);
    }
    return null;
}

/** Build a portable SAML profile from Firebase additional user info / token claims. */
export function extractSamlProfile(result, decodedTokenClaims = null) {
    const additional = getAdditionalUserInfo(result);
    const fromProfile = additional?.profile || {};
    const fromToken = decodedTokenClaims?.firebase?.sign_in_attributes || {};
    const attrs = { ...fromToken, ...fromProfile };

    const firstName = safeString(attrs.firstName || attrs.givenName || attrs.given_name);
    const lastName = safeString(attrs.lastName || attrs.sn || attrs.surname || attrs.family_name);
    const jobTitle = safeString(attrs.jobTitle || attrs.title);
    const email = safeString(attrs.email || result.user?.email);
    const groupMembership = normalizeGroupMembership(attrs.groupMembership);
    const displayName = [firstName, lastName].filter(Boolean).join(' ') || null;

    return {
        email,
        firstName,
        lastName,
        jobTitle,
        groupMembership,
        displayName
    };
}

function applyProfileToWindow(user, profile) {
    window.user = user;
    window.userProfile = profile || null;
    if (profile?.name || profile?.displayName) {
        const name = profile.name || profile.displayName;
        // Ensure navbar helpers see a name even if Auth displayName is still empty
        if (!user.displayName && name) {
            Object.defineProperty(user, 'displayName', {
                configurable: true,
                get: () => name
            });
        }
    }
}

async function exchangeForSession(user, samlProfile = null) {
    const token = await user.getIdToken(true);
    const data = await postIdTokenToSessionLogin(token, samlProfile);
    const profile = data?.profile || samlProfile;

    if (profile?.name || profile?.displayName) {
        const name = profile.name || profile.displayName;
        try {
            if (user.displayName !== name) {
                await updateProfile(user, { displayName: name });
            }
        } catch (err) {
            console.warn('Could not set client displayName:', err);
        }
    }

    applyProfileToWindow(auth.currentUser || user, profile);
    return auth.currentUser || user;
}

/**
 * Sign in with DHSB SAML via popup.
 * Prefer popup over redirect: account.dhsb.org + authDomain login.dhsb.org breaks
 * redirect sessionStorage (partitioned / cross-subdomain), which causes
 * "missing initial state" on /__/auth/handler.
 */
export function signInWithSaml() {
    return new Promise((resolve, reject) => {
        signInWithPopup(auth, samlProvider).then(async function (result) {
            console.log(`Authed with ${result.user.email}`);
            try {
                const samlProfile = extractSamlProfile(result);
                console.log('SAML profile:', samlProfile);
                resolve(await exchangeForSession(result.user, samlProfile));
            } catch (error) {
                console.error(error);
                reject(error);
            }
        }).catch(function (error) {
            console.error(error);
            reject(error);
        });
    });
}

export function signInWithToken(token, profile = null) {
    return new Promise((resolve, reject) => {
        signInWithCustomToken(auth, token).then(async function (result) {
            console.log(`Authed with ${result.user.email}`);
            applyProfileToWindow(result.user, profile);
            if (profile?.name && !result.user.displayName) {
                try {
                    await updateProfile(result.user, { displayName: profile.name });
                    applyProfileToWindow(auth.currentUser || result.user, profile);
                } catch (err) {
                    console.warn('Could not set displayName after custom token:', err);
                }
            }
            resolve(auth.currentUser || result.user);
        }).catch(function (error) {
            console.error(error);
            reject(error);
        });
    });
}
